import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  FiMapPin,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  Input,
  AsyncSelect,
  chakraComponents,
  useBreakpointValue,
  DropdownIndicatorProps,
  VisuallyHidden
} from "@/storybook-components";
import { getSuburbs } from "@/models";
import type { Question, Suburb } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

interface RegionQuestionProps {
  question: Question;
}

export const validate = async (
  value: string,
  question: Question
): Promise<string[]> => {
  let schema = string().nullable();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(value);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const RegionQuestion = ({ question }: RegionQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { site } = useAppSelector((state) => state.site);
  const size = useBreakpointValue({ base: "lg", lg: "md" });

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";
  const suburb = answer ? { label: answer, value: answer } : null;

  const handleSelect = (e: { value: string }): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e?.value ? e.value : null
      })
    );
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  const fetchSuburbsData = async (query: string) => {
    const { id } = question;
    return getSuburbs(token, baseUrl, {
      filterable: true,
      filters: {
        search: query,
        question_id: id
      }
    }).then((suburbs) =>
      suburbs.map((suburb: Suburb) => {
        const { name, state, zipcode, postalCode } = suburb;
        let regionValue = name;

        if (state) {
          regionValue = `${regionValue}, ${state}`;
        }

        if (postalCode) {
          regionValue = `${regionValue}, ${postalCode}`;
        }

        if (zipcode) {
          regionValue = `${regionValue} ${zipcode}`;
        }

        return {
          label: regionValue,
          value: regionValue
        };
      })
    );
  };

  const handleNoOptionsMessage = (e: { inputValue: string }): string => {
    if (!e.inputValue)
      return translate("questions.region.enterYourPostalDetails");

    return translate("questions.region.noMatchingPostalDetails");
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      {site?.region === "UNITED_KINGDOM" ? (
        <Input
          type="text"
          value={answer}
          onChange={handleChange}
          size={{ base: "lg", lg: "md" }}
          data-testid="regionQuestion-input"
        />
      ) : (
        <AsyncSelect
          size={size}
          isClearable
          cacheOptions
          value={suburb}
          menuPlacement="auto"
          onChange={handleSelect}
          loadOptions={fetchSuburbsData}
          noOptionsMessage={handleNoOptionsMessage}
          placeholder={translate("questions.region.selectOption")}
          innerProps={{ "data-testid": "regionQuestion-asyncSelect" }}
          loadingMessage={() => translate("common.loading")}
          components={{
            DropdownIndicator: (props: DropdownIndicatorProps) => (
              <chakraComponents.DropdownIndicator {...props}>
                <FiMapPin />
              </chakraComponents.DropdownIndicator>
            )
          }}
        />
      )}
      {error.length > 0 && (
        <FormErrorMessage data-testid="regionQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default RegionQuestion;
